import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import * as Sentry from '@sentry/react'

/** Request body for POST /validation/patient-demographics (Carebus/Individual schema). */
export interface PatientDemographicsValidationRequest {
  name: {
    first: string
    last: string
  }
  dateOfBirth: string
  MRN: string
}

/** Success response from patient demographics validation. */
export interface PatientDemographicsValidationResponse {
  valid: boolean
}

/** 422 error body may contain validation details. */
export interface PatientDemographicsValidationErrorBody {
  detail?: string | Array<{ msg?: string; loc?: string[] }>
  message?: string
}

export class PatientDemographicsValidationError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: PatientDemographicsValidationErrorBody
  ) {
    super(message)
    this.name = 'PatientDemographicsValidationError'
  }
}

/**
 * Validate patient demographics (name, DOB, MRN) via server before submission.
 * Rules match the Carebus/Individual schema used for order creation.
 *
 * @returns Promise that resolves with { valid: true } on success
 * @throws PatientDemographicsValidationError on 422 with server validation details
 * @throws on network or other API errors
 */
export async function validatePatientDemographics(
  payload: PatientDemographicsValidationRequest
): Promise<PatientDemographicsValidationResponse> {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /validation/patient-demographics',
    },
    async (span) => {
      const client = ProviderAPIClient.getInstance()
      const response = await client.post('/validation/patient-demographics', payload, {
        'Content-Type': 'application/json',
      })

      if (response.ok) {
        const data = (await response.json()) as PatientDemographicsValidationResponse
        span.setAttribute('valid', data.valid)
        return data
      }

      const status = response.status
      span.setAttribute('http.status_code', status)

      let body: PatientDemographicsValidationErrorBody | undefined
      try {
        body = (await response.json()) as PatientDemographicsValidationErrorBody
      } catch {
        body = undefined
      }

      const message =
        (body && typeof body.detail === 'string' && body.detail) ||
        (body && typeof body.message === 'string' && body.message) ||
        (body && Array.isArray(body.detail) && body.detail.map((d) => d.msg ?? JSON.stringify(d)).join('; ')) ||
        `Patient demographics validation failed (${status})`

      const err = new PatientDemographicsValidationError(message, status, body)
      Sentry.captureException(err, {
        tags: { endpoint: 'validation/patient-demographics', status: String(status) },
      })
      throw err
    }
  )
}
